// react、next
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Loader, LoadingOverlay } from '@mantine/core';

// component
import GameDetail from 'components/GameDetail';
import GameDetailMobile from 'components/GameDetailMobile';

// store
import { setCurrentGame, setLoading } from '@/store/features/gameSlice';
import { wrapper } from '@/store';

// scss
import styles from './index.module.scss';

// utils，const
import { cleanData, ensureAbsoluteImageUrl, formatGameData, formatSingleGameData } from "@/utils/methods";
import grpcApi from '@/lib/api/grpcApi';

// 游戏详情页
export function GameDetailPage({ similarGames, currentGame: serverCurrentGame }) {
    const router = useRouter();
    const path = router.query.slug;
    const dispatch = useDispatch();
    const { currentGame, loading, isMobile } = useSelector((state) => state.game);

    // 如果服务端传递了currentGame，则使用服务端数据
    const gameToShow = serverCurrentGame || currentGame;

    // 等待加载的element
    const loadingEle = (
        <div className={styles.container}>
            <LoadingOverlay
                visible={loading}
                zIndex={2000}
                overlayProps={{ radius: 'md', opacity: 0 }}
                loaderProps={{ color: 'white', type: 'bars' }}
            />
        </div>
    )

    if (loading) {
        return loadingEle;
    }

    if (!gameToShow) {
        return loadingEle;
    } else {
        return (
            <>
                {isMobile ? (
                    <GameDetailMobile
                        game={gameToShow}
                        similarGamesProps={similarGames}
                    />
                ) : (
                    <GameDetail
                        game={gameToShow}
                        similarGamesProps={similarGames}
                    />
                )}
            </>
        );
    }
}

// 添加服务端数据预取
export const getGamePageServerSideProps = wrapper.getServerSideProps(
    (store) => async (context) => {
        const { slug } = context.params;

        // 获取当前store状态
        const state = store.getState();
        const { currentGame } = state.game;

        // 在服务端导入必要的类型和客户端
        const { grpcClientManager } = await import('@/lib/grpc/client-manager');
        const { SupportedLanguage } = await import('@/generated/localization');
        const { GameStatus } = await import('@/generated/game');



        // 格式化相似游戏数据
        const formatSimilarGames = (games) => {
            if (!games || !Array.isArray(games)) return [];

            return games.map(game => formatSingleGameData(game)).filter(Boolean);
        };

        try {
            const gameClient = grpcClientManager.getGameClient();
            let gameData = null;
            let gameId = null;

            // 1. 如果store中有currentGame，使用其id获取游戏数据
            if (currentGame && currentGame.id) {
                gameId = currentGame.id;

                // 通过ID获取游戏详情
                const gameRequest = {
                    id: gameId,
                    slug: '',
                    language: SupportedLanguage.LANG_EN_US
                };

                const gameResponse = await gameClient.getGame(gameRequest);
                gameData = formatGameData(gameResponse.game);

                if (gameData) {
                    store.dispatch(setCurrentGame(gameData));
                }
            } else {
                // 2. 如果store中没有currentGame，使用slug搜索游戏
                const gameRequest = {
                    id: 0,
                    slug: slug,
                    language: SupportedLanguage.LANG_EN_US
                };

                const gameResponse = await gameClient.getGame(gameRequest);
                gameData = formatSingleGameData(gameResponse.game);

                if (!gameData) {
                    // 3. 如果没有找到游戏，跳转到404
                    return {
                        notFound: true,
                    };
                }

                gameId = gameData.id;

                if (gameData) {
                    store.dispatch(setCurrentGame(gameData));
                }
            }

            // 4. 获取相关推荐游戏
            let similarGames = [];
            if (gameId) {
                try {
                    // 使用gRPC客户端获取相关推荐游戏
                    const gameClient = grpcClientManager.getGameClient();
                    const relatedRequest = {
                        id: gameId,
                        limit: 18,
                        language: SupportedLanguage.LANG_EN_US
                    };

                    const relatedResponse = await gameClient.getRelatedRecommendedGames(relatedRequest);
                    similarGames = formatSimilarGames(relatedResponse.games);
                } catch (error) {
                    console.error('Failed to fetch related games:', error);
                    similarGames = [];
                }
            }

            return {
                props: cleanData({
                    similarGames: similarGames,
                    currentGame: gameData
                })
            };

        } catch (error) {
            console.error('Failed to fetch game data:', error);
            console.error('错误详情:', {
                message: error.message,
                stack: error.stack,
                code: error.code,
                details: error.details
            });

            // 如果是因为游戏不存在导致的错误，跳转到404
            if (error.code === 'NOT_FOUND' || error.message?.includes('not found')) {
                return {
                    notFound: true,
                };
            }

            return {
                props: cleanData({
                    similarGames: [],
                    currentGame: null
                })
            };
        }
    }
);